import { type FilterLinkOf, useFilterUnits, withUrlPrefix } from 'noddi-ui-web';
import { bookingFilterUnit, bookingToolbarDateRange } from './bookingFilterSet';
import {
  bookingCancellationSourceFilter,
  bookingCustomerGroupTypesFilter,
  bookingEventFilters,
  bookingFlagFilters,
  bookingLocationTypeFilter,
  bookingOrderPaymentStatusesFilter,
  bookingPaymentOptionsFilter,
  bookingSegmentFilter,
  bookingTimingFilters
} from './bookingFilterSpecs';
import {
  bookingStatusFilter,
  unableToCompleteClearingStatusFilter,
  unableToCompleteStatusFilter,
  upcomingStatusOnlyFilter
} from './bookingStatusFilters';
import { useBookingCustomerRelationFilters } from './useBookingCustomerRelationFilters';
import { useBookingFilterSpecs } from './useBookingFilterSpecs';

type Args = {
  /** The partner's selected departments: scope every request and the option lists. */
  serviceDepartmentIds: number[];
};

/**
 * Partner booking list and its customer, car and worker tabs; callers add only their own scope. Resolved here,
 * so a caller combines the unit with its locked scope.
 */
export const usePartnerBookingFilters = ({ serviceDepartmentIds }: Args) => {
  const specs = usePartnerListFilters(serviceDepartmentIds, unableToCompleteClearingStatusFilter);

  return useFilterUnits([
    bookingFilterUnit.locked({ serviceDepartmentIds }),
    bookingToolbarDateRange,
    bookingStatusFilter({ withQuickFilter: true }),
    bookingFlagFilters.isPickupBooking,
    bookingFlagFilters.isDropIn,
    bookingFlagFilters.isFleetScheduleBooking,
    bookingCancellationSourceFilter,
    ...specs
  ]);
};

/** What a link into the partner bookings list may set (`filterSearch`). */
export type PartnerBookingListLink = FilterLinkOf<typeof usePartnerBookingFilters>;

/** Dates of the bulk reschedule dialog, which opens over the bookings list, on its own URL keys (`br-from-date`). */
const bulkRescheduleDates = withUrlPrefix('br', bookingToolbarDateRange);

/**
 * Partner filters scoped to what can be rescheduled: status is locked to upcoming, and cancellation source
 * never matches it. Its unable-to-complete control leaves the main list's status alone.
 */
export const useBulkRescheduleBookingFilters = ({ serviceDepartmentIds }: Args) => {
  const specs = usePartnerListFilters(serviceDepartmentIds, unableToCompleteStatusFilter);

  return useFilterUnits([
    bookingFilterUnit.locked({ serviceDepartmentIds }),
    // Own URL keys, so picking dates in the dialog leaves the bookings list behind it unchanged.
    bulkRescheduleDates,
    upcomingStatusOnlyFilter,
    bookingFlagFilters.isPickupBooking,
    bookingFlagFilters.isDropIn,
    bookingFlagFilters.isFleetScheduleBooking,
    ...specs
  ]);
};

/** Filters both partner lists show, in sidebar order, after their own status controls. */
const usePartnerListFilters = (
  serviceDepartmentIds: number[],
  unableToCompleteStatus: typeof unableToCompleteClearingStatusFilter | typeof unableToCompleteStatusFilter
) => {
  const core = useBookingFilterSpecs({ serviceDepartmentIds });
  const relations = useBookingCustomerRelationFilters();
  const { eventTypes, eventSources, hasSalesItemChange, hasTimeChange, hasAdminNote, hasCustomerNote, hasWorkerNote } =
    bookingEventFilters;

  return [
    bookingCustomerGroupTypesFilter,
    core.organization,
    relations.organizationTypes,
    core.groups,
    ...relations.payerAndCustomerData,
    core.driver,
    bookingSegmentFilter,
    core.brand,
    bookingLocationTypeFilter,
    bookingPaymentOptionsFilter,
    bookingOrderPaymentStatusesFilter,
    unableToCompleteStatus,
    core.products,
    core.serviceAreas,
    core.garage,
    core.sitePort,
    core.workstation,
    core.carMake,
    core.carModel,
    eventTypes,
    eventSources,
    hasSalesItemChange,
    hasTimeChange,
    hasAdminNote,
    hasCustomerNote,
    hasWorkerNote,
    ...bookingTimingFilters
  ] as const;
};
